import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import { showError, showSuccess } from '@/lib/dialogHelper'
import EditButton from '@/components/EditButton'
import DeleteButton from '@/components/DeleteButton'
import SyncButton from '@/components/SyncButton'
import InvoicesService from '@/services/InvoicesService'
import SettingsService from '@/services/SettingsService'

import useSettings from '@/hooks/useSettings'
import CompanyFormModal, { createCompanyId, emptyCompany } from './components/CompanyFormModal'

const COMPANY_FIELDS = [
	'companyName',
	'companyAddress',
	'companyTaxNo',
	'companyTel',
	'companyEmail',
	'companyUrl',
	'companyInfaktApi',
]

function normalizeCompaniesFromSettings(settings) {
	if (Array.isArray(settings?.companies) && settings.companies.length > 0) {
		return settings.companies.map(company => ({
			...emptyCompany(),
			...company,
			id: company.id || createCompanyId(),
		}))
	}

	const hasLegacyData = COMPANY_FIELDS.some(key => settings?.[key]?.toString().trim())
	if (!hasLegacyData) {
		return []
	}

	return [{
		id: createCompanyId(),
		...COMPANY_FIELDS.reduce((acc, key) => {
			acc[key] = settings?.[key] || ''
			return acc
		}, {}),
	}]
}

const CompanySettingsTab = () => {
	const { t } = useTranslation()

	const { settings, loading, saving, error, success, updateSettings, clearError, clearSuccess } = useSettings()

	const [companies, setCompanies] = useState([])
	const [formOpen, setFormOpen] = useState(false)
	const [formMode, setFormMode] = useState('add')
	const [formData, setFormData] = useState(emptyCompany())
	const [editingCompanyId, setEditingCompanyId] = useState(null)
	const [syncingCompanyId, setSyncingCompanyId] = useState(null)
	const [deletingCompanyId, setDeletingCompanyId] = useState(null)

	useEffect(() => {
		setCompanies(normalizeCompaniesFromSettings(settings))
	}, [settings])

	useEffect(() => {
		if (error) showError(t('settingsPage.company.messages.saveError', { error }), clearError)
	}, [error, t, clearError])

	useEffect(() => {
		if (success) showSuccess(t('settingsPage.company.messages.saveSuccess'), clearSuccess)
	}, [success, t, clearSuccess])

	const openForm = (mode, company = null) => {
		setFormMode(mode)
		setEditingCompanyId(company?.id ?? null)
		setFormData(company ? { ...company } : emptyCompany())
		setFormOpen(true)
	}

	const handleInputChange = (e) => {
		const { name, value } = e.target
		setFormData(prevState => ({
			...prevState,
			[name]: value,
		}))
	}

	const saveCompanies = (updatedCompanies) => {
		setCompanies(updatedCompanies)
		updateSettings({ ...settings, companies: updatedCompanies })
	}

	const handleFormSubmit = (e) => {
		e.preventDefault()

		const updatedCompanies = formMode === 'add'
			? [...companies, formData]
			: companies.map(company => (
				company.id === editingCompanyId ? { ...formData } : company
			))

		setFormOpen(false)
		saveCompanies(updatedCompanies)
	}

	const handleRemove = async (company) => {
		const result = await Swal.fire({
			title: t('notifications.confirmTitle'),
			html: t('settingsPage.company.messages.removeConfirmWithDocs', {
				name: company.companyName || company.id,
				defaultValue: t('settingsPage.company.messages.removeConfirm', {
					name: company.companyName || company.id,
				}),
			}),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('notifications.confirmDeleteButton'),
			cancelButtonText: t('notifications.cancelButton'),
		})

		if (!result.isConfirmed) return

		setDeletingCompanyId(company.id)
		try {
			const { success: docsOk, error: docsError, data } = await SettingsService.deleteCompanyDocuments(company.id)
			if (!docsOk) {
				showError(t('settingsPage.company.messages.removeDocsError', {
					error: docsError || 'Unknown error',
				}))
				return
			}

			saveCompanies(companies.filter(item => item.id !== company.id))
			showSuccess(t('settingsPage.company.messages.removeSuccess', {
				name: company.companyName || company.id,
				deletedInvoices: data?.deletedInvoices ?? 0,
				deletedKorekty: data?.deletedKorekty ?? 0,
			}))
		} catch (err) {
			showError(t('settingsPage.company.messages.removeDocsError', {
				error: err.message || err,
			}))
		} finally {
			setDeletingCompanyId(null)
		}
	}

	const handleSynchronize = async (company) => {
		if (!company?.id) {
			showError(t('settingsPage.company.messages.syncError', { error: 'Missing company id' }))
			return
		}

		setSyncingCompanyId(company.id)
		try {
			const { success, error, data } = await InvoicesService.pullSynchronize(company.id)
			if (success) {
				showSuccess(t('settingsPage.company.messages.syncSuccess', {
					name: company.companyName || company.id,
					remoteCount: data?.remoteCount ?? 0,
					upserted: data?.upserted ?? 0,
					correctionsRemoteCount: data?.corrections?.remoteCount ?? 0,
					correctionsUpserted: data?.corrections?.upserted ?? 0,
				}))
			} else {
				showError(t('settingsPage.company.messages.syncError', { error }))
			}
		} catch (err) {
			showError(t('settingsPage.company.messages.syncError', {
				error: err.message || err,
			}))
		} finally {
			setSyncingCompanyId(null)
		}
	}

	if (loading) {
		return <LoadingIndicator message={t('settingsPage.company.loading')} />
	}

	return (
		<div className="w-full px-4 py-16 sm:px-6 lg:px-8">
			<div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<h2 className="text-base/7 font-semibold text-gray-900">{t('settingsPage.company.title')}</h2>
					<p className="mt-1 text-sm/6 text-gray-500">{t('settingsPage.company.subtitle')}</p>
				</div>
				<button
					type="button"
					onClick={() => openForm('add')}
					className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
				>
					+ {t('settingsPage.company.actions.addCompany')}
				</button>
			</div>

			<div className="overflow-x-auto rounded-lg border border-gray-200">
				<table className="min-w-full w-full text-sm text-left shadow-sm text-gray-500">
					<thead className="border-b border-gray-200 bg-gray-50">
						<tr>
							<th className="px-6 py-3">{t('settingsPage.company.table.name')}</th>
							<th className="px-6 py-3">{t('settingsPage.company.table.taxNo')}</th>
							<th className="px-6 py-3">{t('settingsPage.company.table.email')}</th>
							<th className="px-6 py-3">{t('settingsPage.company.table.tel')}</th>
							<th className="px-6 py-3 text-right">{t('settingsPage.company.table.actions')}</th>
						</tr>
					</thead>
					<tbody>
						{companies.map(company => (
							<tr
								key={company.id}
								className="bg-white hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
							>
								<td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
									{company.companyName || '-'}
								</td>
								<td className="px-6 py-4 whitespace-nowrap">{company.companyTaxNo || '-'}</td>
								<td className="px-6 py-4">{company.companyEmail || '-'}</td>
								<td className="px-6 py-4 whitespace-nowrap">{company.companyTel || '-'}</td>
								<td className="px-6 py-4 text-right">
									<div className="flex justify-end space-x-2">
										<EditButton
											onClick={() => openForm('edit', company)}
											title={t('settingsPage.company.actions.editTitle')}
											label={t('settingsPage.company.actions.edit')}
										/>
										<DeleteButton
											onClick={() => handleRemove(company)}
											disabled={deletingCompanyId !== null}
											title={t('settingsPage.company.actions.deleteTitle')}
											label={deletingCompanyId === company.id
												? t('settingsPage.company.actions.deleting')
												: t('settingsPage.company.actions.delete')}
										/>
										{company.companyInfaktApi?.toString().trim() && (
											<SyncButton
												onClick={() => handleSynchronize(company)}
												isLoading={syncingCompanyId === company.id}
												disabled={syncingCompanyId !== null || deletingCompanyId !== null}
												title={t('settingsPage.company.actions.syncTitle')}
												label={t('settingsPage.company.actions.sync')}
												loadingLabel={t('settingsPage.company.actions.syncing')}
											/>
										)}
									</div>
								</td>
							</tr>
						))}
						{companies.length === 0 && (
							<tr>
								<td colSpan={5} className="px-6 py-4 text-center text-gray-600">
									{t('settingsPage.company.empty')}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<CompanyFormModal
				isOpen={formOpen}
				formMode={formMode}
				formData={formData}
				saving={saving}
				onInputChange={handleInputChange}
				onClose={() => setFormOpen(false)}
				onSubmit={handleFormSubmit}
			/>
		</div>
	)
}

export default CompanySettingsTab
